import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "../supabaseClient";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  tosAccepted: boolean;
  acceptTos: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const tosAccepted = !!(user?.user_metadata?.tos_accepted_at);

  const acceptTos = async () => {
    if (!supabase) return;
    await supabase.auth.updateUser({
      data: { tos_accepted_at: new Date().toISOString() },
    });
  };

  const signOut = async () => {
    await supabase?.auth.signOut({ scope: "local" });
  };

  return (
    <AuthContext.Provider value={{ user, loading, tosAccepted, acceptTos, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
